// src/pages/TreasurerReport.jsx
// Adds "Preview" for bills:
// - In the Add Entry form (under the file field)
// - In both Income & Expense tables (for saved + unsaved rows)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { FaArrowLeft } from "react-icons/fa";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

function readUserFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem("user")) || {};
    return { name: "Unknown User", role: "Member", image: "/user.png", ...raw };
  } catch {
    return { name: "Unknown User", role: "Member", image: "/user.png" };
  }
}
const currency = (n) =>
  (Number(n || 0)).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const toJsonSafe = async (res) => {
  const t = await res.text();
  try {
    return JSON.parse(t);
  } catch {
    throw new Error(t.slice(0, 400));
  }
};

const isLikelyImage = (name = "", mime = "") => {
  const n = (name || "").toLowerCase();
  const m = (mime || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp)$/i.test(n) || m.startsWith("image/");
};

export default function TreasurerReport() {
  const params = useParams();
  const projectId = useMemo(() => {
    const raw = params.projectId ?? params.id ?? params.pid ?? null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [params]);

  const user = readUserFromStorage();

  // Project header
  const [projectName, setProjectName] = useState("");
  const [projectPhase, setProjectPhase] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!projectId) return;
    (async () => {
      try {
        const res = await fetch(
          `${ABSOLUTE_HOST}/Backend/project_api.php?action=get_details&id=${projectId}`
        );
        const data = await toJsonSafe(res);
        if (cancelled) return;
        if (data?.success) {
          setProjectName(data.project?.name || `Project ${projectId}`);
          const p = data.project || {};
          const phase =
            Number.isFinite(+p.phase_count) && +p.phase_count > 0 ? +p.phase_count : 1;
          setProjectPhase(phase);
        } else {
          setProjectName(`Project ${projectId}`);
        }
      } catch {
        setProjectName(`Project ${projectId}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Entries
  const [entries, setEntries] = useState([]);
  const [type, setType] = useState("income");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [file, setFile] = useState(null);

  // NEW: preview of the *selected* bill (before adding)
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState("");

  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(!!projectId);
  const [savingAll, setSavingAll] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);

  const fileInputRef = useRef(null);

  // Load rows
  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setEntries([]);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${ABSOLUTE_HOST}/Backend/finance_api.php?action=list&project_id=${projectId}`
        );
        const data = await toJsonSafe(res);
        if (cancelled) return;
        if (!data?.success) throw new Error(data?.message || "Load failed");
        const mapped = (data.items || []).map((r) => ({
          id: r.id,
          type: r.type,
          amount: Number(r.amount || 0),
          description: r.description || "",
          date: r.txn_date,
          bill_url: r.bill_url || "",
          bill_filename: r.bill_filename || "",
          bill_mime: r.bill_mime || "",
          saved: true,
        }));
        setEntries(mapped);
      } catch (e) {
        console.error("list error:", e);
        alert("Failed to load finance entries.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Revoke selected file preview URL on change/unmount
  useEffect(() => {
    return () => {
      if (selectedPreviewUrl) URL.revokeObjectURL(selectedPreviewUrl);
    };
  }, [selectedPreviewUrl]);

  // Filter/search
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return entries;
    return entries.filter(
      (e) =>
        (e.description || "").toLowerCase().includes(s) ||
        (e.type || "").toLowerCase().includes(s)
    );
  }, [entries, q]);

  // Split lists
  const incomes = useMemo(
    () => filtered.filter((e) => e.type === "income"),
    [filtered]
  );
  const expenses = useMemo(
    () => filtered.filter((e) => e.type === "expense"),
    [filtered]
  );

  const totals = useMemo(() => {
    let inc = 0,
      exp = 0;
    for (const e of filtered) {
      if (e.type === "income") inc += Number(e.amount || 0);
      else if (e.type === "expense") exp += Number(e.amount || 0);
    }
    return { income: inc, expense: exp, net: inc - exp };
  }, [filtered]);

  const resetForm = () => {
    setType("income");
    setAmount("");
    setDescription("");
    setDate(new Date().toISOString().slice(0, 10));
    setFile(null);
    if (selectedPreviewUrl) URL.revokeObjectURL(selectedPreviewUrl);
    setSelectedPreviewUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const addEntry = () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      alert("Amount must be greater than 0");
      return;
    }
    const tempId = Date.now() + Math.random();
    const billLocalUrl = file ? (selectedPreviewUrl || URL.createObjectURL(file)) : "";
    setEntries((prev) => [
      ...prev,
      {
        tempId,
        type,
        amount: amt,
        description: description.trim(),
        date,
        file,
        billLocalUrl,
        bill_filename: file?.name || "",
        bill_mime: file?.type || "",
        saved: false,
      },
    ]);
    // Keep the blob URL used in list; do NOT revoke here if we used selectedPreviewUrl.
    setSelectedPreviewUrl("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setType("income");
    setAmount("");
    setDescription("");
    setDate(new Date().toISOString().slice(0, 10));
  };

  const deleteEntry = async (row) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      if (row.saved && projectId) {
        const form = new FormData();
        form.append("id", String(row.id));
        form.append("project_id", String(projectId));
        const res = await fetch(
          `${ABSOLUTE_HOST}/Backend/finance_api.php?action=delete_entry`,
          { method: "POST", body: form }
        );
        const data = await toJsonSafe(res);
        if (!data?.success) {
          const detail = data?.error || data?.message || "Delete failed";
          throw new Error(detail);
        }
      }
      setEntries((prev) =>
        prev.filter((e) => (row.saved ? e.id !== row.id : e.tempId !== row.tempId))
      );
    } catch (e) {
      console.error("delete error:", e);
      alert(e?.message || "Failed to delete. Please try again.");
    }
  };

  // Save all unsaved rows
  const saveAll = async () => {
    if (!projectId) {
      alert("Missing or invalid project ID in the URL.");
      return;
    }
    const unsaved = entries.filter((e) => !e.saved);
    if (!unsaved.length) {
      alert("Nothing to save.");
      return;
    }
    try {
      setSavingAll(true);
      for (const e of unsaved) {
        const fd = new FormData();
        fd.append("project_id", String(projectId));
        fd.append("type", e.type);
        fd.append("amount", String(e.amount));
        fd.append("description", e.description || "");
        fd.append("txn_date", e.date);
        fd.append("created_by", user?.SLIITID || "");
        if (e.file) fd.append("bill", e.file);

        const res = await fetch(
          `${ABSOLUTE_HOST}/Backend/finance_api.php?action=add_entry`,
          { method: "POST", body: fd }
        );
        const data = await toJsonSafe(res);
        if (!data?.success) {
          const detail = data?.error || data?.message || "Save failed";
          throw new Error(detail);
        }

        setEntries((prev) =>
          prev.map((row) => {
            if (row.saved) return row;
            if (row.tempId !== e.tempId) return row;
            return {
              id: data.id,
              type: e.type,
              amount: e.amount,
              description: e.description,
              date: e.date,
              bill_url: data.bill_url || "",
              bill_filename: e.file?.name || "",
              bill_mime: e.file?.type || "",
              saved: true,
            };
          })
        );
      }
      alert("All entries saved!");
    } catch (err) {
      console.error("saveAll error:", err);
      alert(`Failed to save all entries.\n${err?.message || "Please try again."}`);
    } finally {
      setSavingAll(false);
    }
  };

  const exportCSV = () => {
    const rows = [
      ["Date", "Type", "Description", "Amount", "Bill Link / File"],
      ...entries.map((e) => [
        e.date,
        e.type,
        e.description,
        e.amount,
        e.saved ? e.bill_url || "" : e.file?.name || "",
      ]),
    ];
    const csv = rows
      .map((r) =>
        r.map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `treasurer_report_project_${projectId ?? "unknown"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Excel export (kept)
  const exportExcel = () => {
    const esc = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    const rowHtml = (e) => {
      const link =
        e.saved && e.bill_url
          ? `<a href="${esc(e.bill_url)}">${esc(e.bill_filename || "Bill link")}</a>`
          : esc(e.bill_filename || e.file?.name || "");
      return `<tr>
        <td>${esc(e.date)}</td>
        <td>${esc(e.description)}</td>
        <td style="mso-number-format:'0.00'; text-align:right;">${Number(
          e.amount || 0
        ).toFixed(2)}</td>
        <td>${link || "—"}</td>
      </tr>`;
    };

    const incomeRows = incomes.map(rowHtml).join("");
    const expenseRows = expenses.map(rowHtml).join("");

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Report</title></head>
<body>
  <h3>${esc(projectName || `Project ${projectId || ""}`)} — Treasurer Report</h3>
  <p><b>Total Income:</b> LKR ${currency(totals.income)} &nbsp; 
     <b>Total Expense:</b> LKR ${currency(totals.expense)} &nbsp; 
     <b>Net:</b> LKR ${currency(totals.net)}</p>

  <h4>Income</h4>
  <table border="1" cellspacing="0" cellpadding="4">
    <tr><th>Date</th><th>Description</th><th>Amount (LKR)</th><th>Bill</th></tr>
    ${incomeRows || '<tr><td colspan="4">—</td></tr>'}
  </table>

  <br>

  <h4>Expense</h4>
  <table border="1" cellspacing="0" cellpadding="4">
    <tr><th>Date</th><th>Description</th><th>Amount (LKR)</th><th>Bill</th></tr>
    ${expenseRows || '<tr><td colspan="4">—</td></tr>'}
  </table>
</body></html>`;

    const blob = new Blob([html], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `treasurer_report_${projectId ?? "report"}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Helpers for PDF export
  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const urlToImageDataUrl = async (url) => {
    try {
      const resp = await fetch(url, { mode: "cors" });
      const blob = await resp.blob();
      if (!blob.type.startsWith("image/")) return null;
      return await new Promise((resolve) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.readAsDataURL(blob);
      });
    } catch {
      return null; // CORS blocked or non-image
    }
  };

  const exportPDF = async () => {
    setExportingPDF(true);
    try {
      let jsPDFmod;
      try {
        jsPDFmod = await import("jspdf");
      } catch {
        alert(
          "PDF export needs jsPDF.\nPlease run: npm i jspdf\nThen rebuild and try again."
        );
        return;
      }

      const { jsPDF } = jsPDFmod;
      const doc = new jsPDF({ unit: "pt", format: "a4", compressPdf: true });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const marginX = 40;
      const marginY = 40;

      // Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(
        `${projectName || `Project ${projectId || ""}`} — Treasurer Bills`,
        marginX,
        marginY
      );
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(
        `Totals — Income: LKR ${currency(totals.income)}  |  Expense: LKR ${currency(
          totals.expense
        )}  |  Net: LKR ${currency(totals.net)}`,
        marginX,
        marginY + 18
      );

      const grouped = [
        { title: "Income", items: incomes },
        { title: "Expense", items: expenses },
      ];

      let y = marginY + 40;
      for (const group of grouped) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(group.title, marginX, y);
        y += 10;
        doc.setFont("helvetica", "normal");

        let slot = 0; // 0 or 1 (two per page)
        const slotHeight = (pageH - marginY * 2 - 20) / 2;
        const imgMaxW = pageW - marginX * 2;
        const imgMaxH = slotHeight - 80;

        for (const e of group.items) {
          if (slot === 2) {
            doc.addPage();
            y = marginY;
            slot = 0;
          }

          if (slot === 0) {
            // top slot
          } else if (slot === 1) {
            y = marginY + 20 + slotHeight;
          }

          doc.setFontSize(11);
          doc.setTextColor(0, 0, 0);
          doc.text(
            `${e.date}  •  ${e.description || "-"}  •  LKR ${currency(e.amount)}`,
            marginX,
            y
          );
          y += 12;

          let dataUrl = null;
          if (!e.saved && e.file && isLikelyImage(e.file.name, e.file.type)) {
            dataUrl = await fileToDataUrl(e.file);
          } else if (e.saved && e.bill_url) {
            if (isLikelyImage(e.bill_filename, e.bill_mime)) {
              dataUrl = await urlToImageDataUrl(e.bill_url);
            } else {
              dataUrl = await urlToImageDataUrl(e.bill_url);
            }
          }

          if (dataUrl) {
            doc.addImage(
              dataUrl,
              "JPEG",
              marginX,
              y,
              imgMaxW,
              imgMaxH,
              undefined,
              "FAST"
            );
            y += imgMaxH + 6;
          } else {
            const label = e.bill_filename
              ? `Bill file: ${e.bill_filename}`
              : "Bill file (not an image)";
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            const lines = doc.splitTextToSize(label, pageW - marginX * 2);
            doc.text(lines, marginX, y);
            y += Math.min(30, lines.length * 12);
            doc.setTextColor(0, 0, 0);
          }

          const desc = e.description || "";
          if (desc) {
            const lines = doc.splitTextToSize(desc, pageW - marginX * 2);
            doc.text(lines, marginX, y);
            y += Math.min(40, lines.length * 12);
          }

          slot += 1;
          if (slot === 2) {
            y = pageH - marginY; // will trigger addPage on next iteration
          } else {
            y = marginY + 20 + slotHeight;
          }
        }

        if (group !== grouped[grouped.length - 1]) {
          doc.addPage();
          y = marginY;
        }
      }

      doc.save(`treasurer_bills_${projectId || "report"}.pdf`);
    } catch (e) {
      console.error("PDF export error:", e);
      alert(e?.message || "Failed to export PDF.");
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b132b] text-white overflow-x-hidden">
      <Sidebar />
      <Topbar
        user={{
          name: user?.name || "Treasurer",
          role: "Report",
          image: user?.image || "/user.png",
        }}
      />

      <main className="pl-0 md:pl-64 pt-16 md:pt-20 pb-10">
        <div className="mx-auto w-full max-w-screen-2xl px-3 sm:px-5 lg:px-10">
          {/* Header Row */}
          <div
  style={{
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "24px",
  }}
>
  <div
    style={{
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "12px",
    }}
  >
    {/* Back Link */}
    <Link
      to={projectId ? `/project/${projectId}` : `/club-projects`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        color: "rgba(255,255,255,0.8)",
        textDecoration: "none",
        transition: "color 0.2s ease",
      }}
      onMouseOver={(e) => (e.currentTarget.style.color = "white")}
      onMouseOut={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.8)")}
    >
      <FaArrowLeft />
      <span style={{ fontSize: "14px" }}>Back</span>
    </Link>

    {/* Buttons */}
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
      {/* Export CSV */}
      <button
        onClick={exportCSV}
        style={{
          backgroundColor: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.1)",
          padding: "8px 12px",
          borderRadius: "10px",
          fontSize: "14px",
          color: "white",
          transition: "all 0.2s ease",
          cursor: "pointer",
        }}
        onMouseOver={(e) =>
          (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)")
        }
        onMouseOut={(e) =>
          (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)")
        }
      >
        Export CSV
      </button>

      {/* Export Excel */}
      <button
        onClick={exportExcel}
        style={{
          backgroundColor: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.1)",
          padding: "8px 12px",
          borderRadius: "10px",
          fontSize: "14px",
          color: "white",
          transition: "all 0.2s ease",
          cursor: "pointer",
        }}
        onMouseOver={(e) =>
          (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)")
        }
        onMouseOut={(e) =>
          (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)")
        }
      >
        Export Excel
      </button>

      {/* Export PDF */}
      <button
        onClick={exportPDF}
        disabled={exportingPDF}
        style={{
          backgroundColor: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.1)",
          padding: "8px 12px",
          borderRadius: "10px",
          fontSize: "14px",
          color: "white",
          opacity: exportingPDF ? 0.6 : 1,
          cursor: exportingPDF ? "not-allowed" : "pointer",
          transition: "all 0.2s ease",
        }}
        onMouseOver={(e) => {
          if (!exportingPDF)
            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)";
        }}
        onMouseOut={(e) => {
          if (!exportingPDF)
            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
        }}
      >
        {exportingPDF ? "Building PDF…" : "Export PDF"}
      </button>

      {/* Save All */}
      <button
        onClick={saveAll}
        disabled={
          savingAll || !entries.some((e) => !e.saved) || !projectId
        }
        title={
          !projectId
            ? "Invalid project ID in URL"
            : entries.some((e) => !e.saved)
            ? "Save unsaved rows"
            : "No new rows"
        }
        style={{
          backgroundColor: "#4f46e5", // indigo
          padding: "8px 12px",
          borderRadius: "10px",
          fontSize: "14px",
          fontWeight: 500,
          color: "white",
          opacity:
            savingAll || !entries.some((e) => !e.saved) || !projectId ? 0.6 : 1,
          cursor:
            savingAll || !entries.some((e) => !e.saved) || !projectId
              ? "not-allowed"
              : "pointer",
          transition: "all 0.2s ease",
          boxShadow: "0 8px 24px -8px rgba(99,102,241,0.55)",
        }}
        onMouseOver={(e) => {
          if (!savingAll)
            e.currentTarget.style.backgroundColor = "#4338ca";
        }}
        onMouseOut={(e) => {
          if (!savingAll) e.currentTarget.style.backgroundColor = "#4f46e5";
        }}
      >
        {savingAll ? "Saving…" : "Save All"}
      </button>
    </div>
  </div>
</div>


          {/* Title Card */}
<div
  style={{
    backdropFilter: "blur(12px)",
    background: "linear-gradient(90deg, rgba(27,34,51,0.8), rgba(21,27,40,0.8))",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "24px",
    boxShadow: "0 20px 60px -30px rgba(0,0,0,0.6)",
  }}
>
  <h1
    style={{
      fontSize: "20px",
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: "white",
      margin: 0,
    }}
  >
    {projectName || (projectId ? `Project ${projectId}` : "Treasurer Report")}
    {projectId ? ` - Phase ${projectPhase || 1}` : ""}
  </h1>

  <p
    style={{
      color: "rgba(255,255,255,0.6)",
      fontSize: "14px",
      marginTop: "6px",
    }}
  >
    {projectId ? (
      <>Treasurer Report • Track income, expenses, and attach bills.</>
    ) : (
      <>Missing/invalid Project ID in URL.</>
    )}
  </p>
</div>


          {!projectId && (
            <div className="bg-amber-500/10 border border-amber-400/30 text-amber-200 rounded-xl p-4 mb-6">
              Use a URL like{" "}
              <code className="bg-black/20 px-1 py-0.5 rounded">
                /project/123/tresurer
              </code>{" "}
              or define a route param named <code>projectId</code> or{" "}
              <code>id</code>.
            </div>
          )}

          {/* Totals + Search */}
<div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginBottom: 24,
  }}
>
  {/* Total Income */}
  <div
    style={{
      background: "linear-gradient(135deg, #1a1f2e 0%, #111827 100%)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 16,
      padding: 16,
      boxShadow: "0 18px 40px -24px rgba(0,0,0,0.65)",
      backdropFilter: "blur(8px)",
    }}
  >
    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>Total Income</div>
    <div style={{ fontSize: 20, fontWeight: 600, color: "#fff", marginTop: 4 }}>
      LKR {currency(totals.income)}
    </div>
  </div>

  {/* Total Expense */}
  <div
    style={{
      background: "linear-gradient(135deg, #1a1f2e 0%, #111827 100%)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 16,
      padding: 16,
      boxShadow: "0 18px 40px -24px rgba(0,0,0,0.65)",
      backdropFilter: "blur(8px)",
    }}
  >
    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>Total Expense</div>
    <div style={{ fontSize: 20, fontWeight: 600, color: "#fff", marginTop: 4 }}>
      LKR {currency(totals.expense)}
    </div>
  </div>

  {/* Net */}
  <div
    style={{
      background: "linear-gradient(135deg, #1a1f2e 0%, #111827 100%)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 16,
      padding: 16,
      boxShadow: "0 18px 40px -24px rgba(0,0,0,0.65)",
      backdropFilter: "blur(8px)",
    }}
  >
    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>Net</div>
    <div style={{ fontSize: 20, fontWeight: 600, color: "#fff", marginTop: 4 }}>
      LKR {currency(totals.net)}
    </div>
  </div>

  {/* Search */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      background: "linear-gradient(135deg, #1a1f2e 0%, #111827 100%)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 16,
      padding: 10,
      boxShadow: "0 18px 40px -24px rgba(0,0,0,0.65)",
      backdropFilter: "blur(8px)",
    }}
  >
    <input
      value={q}
      onChange={(e) => setQ(e.target.value)}
      placeholder="Search description/type…"
      style={{
        width: "100%",
        background: "transparent",
        border: "none",
        outline: "none",
        padding: "8px 10px",
        color: "#e5e7eb",
        fontSize: 14,
        caretColor: "#818cf8",
      }}
    />
  </div>
</div>


          {/* Add Entry Form */}
<section
  style={{
    background: "linear-gradient(135deg, #1a1f2e 0%, #111827 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    boxShadow: "0 18px 40px -24px rgba(0,0,0,0.65)",
    backdropFilter: "blur(8px)",
  }}
>
  <h2
    style={{
      fontWeight: 600,
      marginBottom: 12,
      color: "rgba(255,255,255,0.95)",
      letterSpacing: ".2px",
      fontSize: 16,
    }}
  >
    Add Entry
  </h2>

  <div
    style={{
      display: "grid",
      gap: 12,
      gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    }}
  >
    {/* type */}
    <select
      value={type}
      onChange={(e) => setType(e.target.value)}
      style={{
        gridColumn: "span 2",
        background: "rgba(11,18,32,0.9)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 12,
        padding: "10px 12px",
        color: "#e5e7eb",
        outline: "none",
        fontSize: 14,
        transition: "border-color .2s, box-shadow .2s",
      }}
      onFocus={(e) =>
        (e.currentTarget.style.boxShadow =
          "0 0 0 3px rgba(99,102,241,0.25)")
      }
      onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
    >
      <option value="income">Income</option>
      <option value="expense">Expense</option>
    </select>

    {/* amount */}
    <input
      type="number"
      min="0"
      step="0.01"
      value={amount}
      onChange={(e) => setAmount(e.target.value)}
      placeholder="Amount (LKR)"
      style={{
        gridColumn: "span 2",
        background: "rgba(11,18,32,0.9)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 12,
        padding: "10px 12px",
        color: "#e5e7eb",
        outline: "none",
        fontSize: 14,
        transition: "border-color .2s, box-shadow .2s",
      }}
      onFocus={(e) =>
        (e.currentTarget.style.boxShadow =
          "0 0 0 3px rgba(99,102,241,0.25)")
      }
      onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
    />

    {/* date */}
    <input
      type="date"
      value={date}
      onChange={(e) => setDate(e.target.value)}
      style={{
        gridColumn: "span 2",
        background: "rgba(11,18,32,0.9)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 12,
        padding: "10px 12px",
        color: "#e5e7eb",
        outline: "none",
        fontSize: 14,
        transition: "border-color .2s, box-shadow .2s",
      }}
      onFocus={(e) =>
        (e.currentTarget.style.boxShadow =
          "0 0 0 3px rgba(99,102,241,0.25)")
      }
      onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
    />

    {/* description */}
    <input
      value={description}
      onChange={(e) => setDescription(e.target.value)}
      placeholder="Description"
      style={{
        gridColumn: "span 3",
        background: "rgba(11,18,32,0.9)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 12,
        padding: "10px 12px",
        color: "#e5e7eb",
        outline: "none",
        fontSize: 14,
        transition: "border-color .2s, box-shadow .2s",
      }}
      onFocus={(e) =>
        (e.currentTarget.style.boxShadow =
          "0 0 0 3px rgba(99,102,241,0.25)")
      }
      onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
    />

    {/* file */}
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
      onChange={(e) => {
        const f = e.target.files?.[0] || null;
        setFile(f);
        if (selectedPreviewUrl) URL.revokeObjectURL(selectedPreviewUrl);
        setSelectedPreviewUrl(f ? URL.createObjectURL(f) : "");
      }}
      style={{
        gridColumn: "span 3",
        background: "rgba(11,18,32,0.9)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 12,
        padding: "10px 12px",
        color: "#e5e7eb",
        outline: "none",
        fontSize: 14,
        transition: "border-color .2s, box-shadow .2s",
      }}
      onFocus={(e) =>
        (e.currentTarget.style.boxShadow =
          "0 0 0 3px rgba(99,102,241,0.25)")
      }
      onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
    />

    {/* buttons */}
    <div
      style={{
        gridColumn: "span 6",
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
      }}
    >
      <button
        onClick={addEntry}
        disabled={!amount || Number(amount) <= 0}
        style={{
          background: "#4f46e5",
          color: "#fff",
          padding: "10px 16px",
          borderRadius: 12,
          border: "none",
          fontSize: 14,
          fontWeight: 600,
          cursor: (!amount || Number(amount) <= 0) ? "not-allowed" : "pointer",
          opacity: (!amount || Number(amount) <= 0) ? 0.6 : 1,
          boxShadow: "0 12px 28px -14px rgba(99,102,241,.55)",
          transition: "transform .08s ease, filter .2s ease",
        }}
        onMouseDown={(e) => (e.currentTarget.style.transform = "scale(.98)")}
        onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        Add Entry
      </button>

      <button
        type="button"
        onClick={resetForm}
        style={{
          background: "rgba(255,255,255,0.08)",
          color: "#e5e7eb",
          padding: "10px 16px",
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.10)",
          fontSize: 14,
          fontWeight: 500,
          cursor: "pointer",
          transition: "background .2s ease, transform .08s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.12)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
        onMouseDown={(e) => (e.currentTarget.style.transform = "scale(.98)")}
        onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        Clear
      </button>
    </div>
  </div>

  {/* Selected file info + PREVIEW link */}
  {file && (
    <div style={{ marginTop: 12, fontSize: 12, color: "rgba(255,255,255,0.65)" }}>
      <span>Selected bill: </span>
      <span style={{ color: "rgba(255,255,255,0.9)" }}>{file.name}</span>
      {selectedPreviewUrl && (
        <a
          style={{ color: "#a5b4fc", textDecoration: "underline", marginLeft: 8 }}
          href={selectedPreviewUrl}
          target="_blank"
          rel="noreferrer"
        >
          Preview
        </a>
      )}
    </div>
  )}
</section>


          {/* Income */}
<section
  style={{
    background: "linear-gradient(135deg,#1a1f2e 0%,#111827 100%)",
    border: "1px solid rgba(255,255,255,.08)",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 18px 40px -24px rgba(0,0,0,.65)",
    backdropFilter: "blur(6px)",
  }}
>
  <h3
    style={{
      fontWeight: 600,
      marginBottom: 12,
      color: "rgba(255,255,255,.92)",
      letterSpacing: ".2px",
      fontSize: 16,
    }}
  >
    Income
  </h3>

  {loading ? (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ height: 20, width: 160, background: "rgba(255,255,255,.1)", borderRadius: 8, animation: "pulse 1.6s ease-in-out infinite" }} />
      <div style={{ height: 20, width: "50%", background: "rgba(255,255,255,.1)", borderRadius: 8, animation: "pulse 1.6s ease-in-out infinite" }} />
    </div>
  ) : (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          fontSize: 14,
          borderCollapse: "separate",
          borderSpacing: 0,
          color: "rgba(255,255,255,.9)",
        }}
      >
        <thead style={{ color: "rgba(255,255,255,.7)" }}>
          <tr>
            {["Date", "Description", "Amount (LKR)", "Bill", "Action"].map((h, i) => (
              <th
                key={h}
                style={{
                  textAlign: i === 2 ? "right" : i === 4 ? "right" : "left",
                  padding: "10px 8px",
                  borderBottom: "1px solid rgba(255,255,255,.10)",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {incomes.map((e) => {
            const key = e.saved ? `s-${e.id}` : `t-${e.tempId}`;
            const previewHref = e.saved ? (e.bill_url || "") : (e.billLocalUrl || "");
            const hasPreview = Boolean(previewHref);
            return (
              <tr key={key} style={{ borderBottom: "1px solid rgba(255,255,255,.06)" }}>
                <td style={{ padding: "10px 8px", whiteSpace: "nowrap", verticalAlign: "top" }}>{e.date}</td>
                <td style={{ padding: "10px 8px", maxWidth: 520, verticalAlign: "top" }}>
                  <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {e.description}
                  </span>
                </td>
                <td style={{ padding: "10px 8px", textAlign: "right", whiteSpace: "nowrap", verticalAlign: "top" }}>
                  {currency(e.amount)}
                </td>
                <td style={{ padding: "10px 8px", verticalAlign: "top" }}>
                  {hasPreview && (
                    <a
                      href={previewHref}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: "#a5b4fc",
                        textDecoration: "underline",
                        fontSize: 12,
                        display: "inline-block",
                        width: "fit-content",
                      }}
                    >
                      Preview
                    </a>
                  )}
                </td>
                <td style={{ padding: "10px 8px", textAlign: "right", verticalAlign: "top" }}>
                  <button
                    onClick={() => deleteEntry(e)}
                    disabled={savingAll}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 10,
                      background: "rgba(255,255,255,.08)",
                      border: "1px solid rgba(255,255,255,.10)",
                      color: "rgba(255,255,255,.9)",
                      cursor: savingAll ? "not-allowed" : "pointer",
                      opacity: savingAll ? .6 : 1,
                      transition: "background .2s",
                    }}
                    onMouseEnter={(ev) => (ev.currentTarget.style.background = "rgba(255,255,255,.12)")}
                    onMouseLeave={(ev) => (ev.currentTarget.style.background = "rgba(255,255,255,.08)")}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}

          {!incomes.length && (
            <tr>
              <td colSpan={5} style={{ color: "rgba(255,255,255,.6)", padding: "16px 8px" }}>
                No income records yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )}
</section>

{/* Expense */}
<section
  style={{
    background: "linear-gradient(135deg,#1a1f2e 0%,#111827 100%)",
    border: "1px solid rgba(255,255,255,.08)",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 18px 40px -24px rgba(0,0,0,0.65)",
    backdropFilter: "blur(6px)",
  }}
>
  <h3
    style={{
      fontWeight: 600,
      marginBottom: 12,
      color: "rgba(255,255,255,.92)",
      letterSpacing: ".2px",
      fontSize: 16,
    }}
  >
    Expense
  </h3>

  {loading ? (
    <div style={{ display: "grid", gap: 10 }}>
      <div style={{ height: 20, width: 160, background: "rgba(255,255,255,.1)", borderRadius: 8, animation: "pulse 1.6s ease-in-out infinite" }} />
      <div style={{ height: 20, width: "50%", background: "rgba(255,255,255,.1)", borderRadius: 8, animation: "pulse 1.6s ease-in-out infinite" }} />
    </div>
  ) : (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          fontSize: 14,
          borderCollapse: "separate",
          borderSpacing: 0,
          color: "rgba(255,255,255,.9)",
        }}
      >
        <thead style={{ color: "rgba(255,255,255,.7)" }}>
          <tr>
            {["Date", "Description", "Amount (LKR)", "Bill", "Action"].map((h, i) => (
              <th
                key={h}
                style={{
                  textAlign: i === 2 ? "right" : i === 4 ? "right" : "left",
                  padding: "10px 8px",
                  borderBottom: "1px solid rgba(255,255,255,.10)",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {expenses.map((e) => {
            const key = e.saved ? `s-${e.id}` : `t-${e.tempId}`;
            const previewHref = e.saved ? (e.bill_url || "") : (e.billLocalUrl || "");
            const hasPreview = Boolean(previewHref);
            return (
              <tr key={key} style={{ borderBottom: "1px solid rgba(255,255,255,.06)" }}>
                <td style={{ padding: "10px 8px", whiteSpace: "nowrap", verticalAlign: "top" }}>{e.date}</td>
                <td style={{ padding: "10px 8px", maxWidth: 520, verticalAlign: "top" }}>
                  <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {e.description}
                  </span>
                </td>
                <td style={{ padding: "10px 8px", textAlign: "right", whiteSpace: "nowrap", verticalAlign: "top" }}>
                  {currency(e.amount)}
                </td>
                <td style={{ padding: "10px 8px", verticalAlign: "top" }}>
                  {hasPreview && (
                    <a
                      href={previewHref}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: "#a5b4fc",
                        textDecoration: "underline",
                        fontSize: 12,
                        display: "inline-block",
                        width: "fit-content",
                      }}
                    >
                      Preview
                    </a>
                  )}
                </td>
                <td style={{ padding: "10px 8px", textAlign: "right", verticalAlign: "top" }}>
                  <button
                    onClick={() => deleteEntry(e)}
                    disabled={savingAll}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 10,
                      background: "rgba(255,255,255,.08)",
                      border: "1px solid rgba(255,255,255,.10)",
                      color: "rgba(255,255,255,.9)",
                      cursor: savingAll ? "not-allowed" : "pointer",
                      opacity: savingAll ? .6 : 1,
                      transition: "background .2s",
                    }}
                    onMouseEnter={(ev) => (ev.currentTarget.style.background = "rgba(255,255,255,.12)")}
                    onMouseLeave={(ev) => (ev.currentTarget.style.background = "rgba(255,255,255,.08)")}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}

          {!expenses.length && (
            <tr>
              <td colSpan={5} style={{ color: "rgba(255,255,255,.6)", padding: "16px 8px" }}>
                No expense records yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )}
</section>

          
        </div>
      </main>
    </div>
  );
}
